import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Play, Loader2, CheckCircle2, AlertTriangle, RefreshCw, ExternalLink,
  ArrowRight, ShieldCheck, GraduationCap, Search,
} from 'lucide-react';
import AppShell from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConfidenceBar = ({ value }) => {
  const clamped = Math.max(0, Math.min(100, value ?? 0));
  const color = clamped >= 80 ? 'bg-emerald-500' : clamped >= 50 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 bg-[#F0EBE1] rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${clamped}%` }} />
      </div>
      <span className="text-xs font-semibold text-[#14213D] tabular-nums w-8">{clamped}</span>
    </div>
  );
};

const StatusBanner = ({ audit, onRun, running }) => {
  if (!audit || audit.status === 'none') {
    return (
      <div className="bg-white border border-[#EBE7E0] rounded-lg p-6 text-center">
        <p className="text-sm text-[#5A6577] mb-4">No audit has been run yet.</p>
        <Button onClick={onRun} disabled={running} className="bg-[#14213D] hover:bg-[#1D2D50] text-[#FDFBF7]" data-testid="audit-run-btn">
          {running ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting…</> : <><Play className="h-4 w-4 mr-2" /> Run audit</>}
        </Button>
      </div>
    );
  }

  const isRunning = audit.status === 'running';
  const pct = audit.total ? Math.round((audit.processed / audit.total) * 100) : 0;

  return (
    <div className="bg-white border border-[#EBE7E0] rounded-lg p-4 sm:p-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          {isRunning ? (
            <Loader2 className="h-5 w-5 text-[#D4AF37] animate-spin" />
          ) : audit.error ? (
            <AlertTriangle className="h-5 w-5 text-red-600" />
          ) : (
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#14213D]">
              {isRunning ? 'Audit in progress' : audit.error ? 'Audit failed' : 'Audit complete'}
            </p>
            <p className="text-xs text-[#5A6577]">
              {isRunning
                ? `Processed ${audit.processed} of ${audit.total} (${pct}%)`
                : `${audit.processed}/${audit.total} items checked · ${audit.flagged_count || 0} flagged`}
            </p>
          </div>
        </div>
        <Button
          onClick={onRun}
          disabled={running || isRunning}
          variant="outline"
          size="sm"
          data-testid="audit-rerun-btn"
        >
          {isRunning ? 'Running…' : <><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Re-run</>}
        </Button>
      </div>
      {isRunning && (
        <div className="mt-3 h-1.5 bg-[#F0EBE1] rounded-full overflow-hidden">
          <div className="h-full bg-[#D4AF37] transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}
      {audit.error && (
        <p className="text-xs text-red-600 mt-3">Error: {audit.error}</p>
      )}
    </div>
  );
};

// ── LoC row ────────────────────────────────────────────────────────────
const LocRow = ({ row, onApply, showApplied }) => {
  const [applying, setApplying] = useState(false);
  const [customUrl, setCustomUrl] = useState('');
  const [editing, setEditing] = useState(false);

  const targetUrl = editing ? customUrl : row.suggested_url;

  const handleApply = async () => {
    if (!targetUrl) {
      toast.error('No suggested URL — enter one manually or skip');
      return;
    }
    setApplying(true);
    try {
      await onApply(row, targetUrl);
    } finally {
      setApplying(false);
    }
  };

  return (
    <article
      className={`bg-white border rounded-lg p-4 space-y-2 ${
        row.applied ? 'border-emerald-200 bg-emerald-50/30' : 'border-[#EBE7E0]'
      }`}
      data-testid={`loc-row-${row.unit_id}-${row.source_index}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[11px] uppercase tracking-wider text-[#5A6577]">
          {row.unit_title} <span className="text-[#D4AF37]">·</span> Source #{row.source_index + 1}
        </p>
        <div className="flex items-center gap-3">
          <ConfidenceBar value={row.confidence} />
          {row.applied && <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-semibold"><CheckCircle2 className="h-3 w-3" /> APPLIED</span>}
        </div>
      </div>

      <p className="font-serif-display text-base text-[#14213D] break-words">{row.source_title}</p>
      <p className="text-xs text-[#5A6577] italic">{row.reason}</p>

      <div className="mt-2 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-2 md:gap-3 items-center">
        <div className="min-w-0 space-y-0.5">
          <p className="text-[11px] uppercase tracking-wider text-[#5A6577]">Current URL</p>
          <a href={row.current_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#14213D] hover:underline inline-flex items-center gap-1 break-all">
            {row.current_url} <ExternalLink className="h-3 w-3 opacity-60 flex-shrink-0" />
          </a>
        </div>
        <ArrowRight className="hidden md:block h-4 w-4 text-[#D4AF37] mx-auto" />
        <div className="min-w-0 space-y-0.5">
          <p className="text-[11px] uppercase tracking-wider text-[#5A6577]">Suggested URL</p>
          {row.suggested_url && !editing ? (
            <a href={row.suggested_url} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1 break-all">
              {row.suggested_url} <ExternalLink className="h-3 w-3 opacity-60 flex-shrink-0" />
            </a>
          ) : (
            <Input
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              onFocus={() => setEditing(true)}
              placeholder="Paste a replacement URL…"
              className="h-8 text-xs"
              data-testid={`loc-custom-url-${row.unit_id}-${row.source_index}`}
            />
          )}
        </div>
      </div>

      {!row.applied && (
        <div className="flex items-center gap-2 pt-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditing((v) => !v)}
            data-testid={`loc-edit-btn-${row.unit_id}-${row.source_index}`}
          >
            {editing ? 'Use suggestion' : 'Enter custom URL'}
          </Button>
          <Button
            size="sm"
            onClick={handleApply}
            disabled={applying || !targetUrl}
            className="bg-[#14213D] hover:bg-[#1D2D50] text-[#FDFBF7]"
            data-testid={`loc-apply-btn-${row.unit_id}-${row.source_index}`}
          >
            {applying ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Applying…</> : 'Apply fix'}
          </Button>
        </div>
      )}
    </article>
  );
};

// ── Grade row ──────────────────────────────────────────────────────────
const GradeRow = ({ row, onApply }) => {
  const [applying, setApplying] = useState(false);
  const [gradeDraft, setGradeDraft] = useState(row.suggested_grade_level || row.current_grade_level);

  const handleApply = async () => {
    if (!gradeDraft || gradeDraft === row.current_grade_level) {
      toast.error('Pick a different grade level');
      return;
    }
    setApplying(true);
    try {
      await onApply(row, Number(gradeDraft));
    } finally {
      setApplying(false);
    }
  };

  return (
    <article
      className={`bg-white border rounded-lg p-4 space-y-2 ${
        row.applied ? 'border-emerald-200 bg-emerald-50/30' : 'border-[#EBE7E0]'
      }`}
      data-testid={`grade-row-${row.unit_id}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[11px] uppercase tracking-wider text-[#5A6577]">
          Unit #{row.unit_number} <span className="text-[#D4AF37]">·</span> currently Grade {row.current_grade_level}
        </p>
        <div className="flex items-center gap-3">
          <ConfidenceBar value={row.confidence} />
          {row.applied && <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-semibold"><CheckCircle2 className="h-3 w-3" /> APPLIED</span>}
        </div>
      </div>

      <p className="font-serif-display text-base text-[#14213D] break-words">{row.unit_title}</p>
      <p className="text-xs text-[#5A6577] italic">{row.reason}</p>

      {!row.applied && (
        <div className="flex items-center gap-2 pt-2 flex-wrap">
          <label className="text-xs text-[#5A6577]">Move to grade:</label>
          <select
            value={gradeDraft || ''}
            onChange={(e) => setGradeDraft(e.target.value)}
            className="border border-[#EBE7E0] rounded px-2 py-1 text-xs bg-white"
            data-testid={`grade-select-${row.unit_id}`}
          >
            {[7, 8, 9, 10, 11, 12].map((g) => (
              <option key={g} value={g}>Grade {g}</option>
            ))}
          </select>
          <Button
            size="sm"
            onClick={handleApply}
            disabled={applying || !gradeDraft || Number(gradeDraft) === row.current_grade_level}
            className="bg-[#14213D] hover:bg-[#1D2D50] text-[#FDFBF7]"
            data-testid={`grade-apply-btn-${row.unit_id}`}
          >
            {applying ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Applying…</> : 'Apply reassignment'}
          </Button>
        </div>
      )}
    </article>
  );
};

// ── Main page ──────────────────────────────────────────────────────────
export default function AdminCurriculumAuditPage() {
  const navigate = useNavigate();
  const [locAudit, setLocAudit] = useState(null);
  const [gradeAudit, setGradeAudit] = useState(null);
  const [runningLoc, setRunningLoc] = useState(false);
  const [runningGrade, setRunningGrade] = useState(false);
  const [search, setSearch] = useState('');
  const [showApplied, setShowApplied] = useState(false);

  const fetchAudits = useCallback(async () => {
    try {
      const [loc, grade] = await Promise.all([
        axios.get(`${API}/admin/audit/loc/latest`),
        axios.get(`${API}/admin/audit/grade/latest`),
      ]);
      setLocAudit(loc.data);
      setGradeAudit(grade.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to load audits');
      }
    }
  }, [navigate]);

  useEffect(() => {
    fetchAudits();
    // Poll every 5s while any audit is running
    const t = setInterval(() => {
      if (locAudit?.status === 'running' || gradeAudit?.status === 'running') fetchAudits();
    }, 5000);
    return () => clearInterval(t);
  }, [fetchAudits, locAudit?.status, gradeAudit?.status]);

  const runAudit = async (kind) => {
    const setter = kind === 'loc' ? setRunningLoc : setRunningGrade;
    setter(true);
    try {
      await axios.post(`${API}/admin/audit/${kind}/run`);
      toast.success(`${kind === 'loc' ? 'LoC' : 'Grade'} audit started — this may take a few minutes.`);
      setTimeout(fetchAudits, 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start audit');
    } finally {
      setter(false);
    }
  };

  const applyLocFix = async (row, newUrl) => {
    try {
      await axios.post(`${API}/admin/audit/apply-loc-fix`, {
        run_id: locAudit.id,
        unit_id: row.unit_id,
        source_index: row.source_index,
        new_url: newUrl,
        new_source_organization: row.suggested_source_organization || '',
      });
      toast.success('URL replaced.');
      await fetchAudits();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to apply fix');
    }
  };

  const applyGradeFix = async (row, newGrade) => {
    try {
      await axios.post(`${API}/admin/audit/apply-grade-fix`, {
        run_id: gradeAudit.id,
        unit_id: row.unit_id,
        new_grade_level: newGrade,
      });
      toast.success('Grade level updated.');
      await fetchAudits();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to apply fix');
    }
  };

  const locFlagged = useMemo(() => {
    const rows = locAudit?.results?.filter((r) => r.flagged || r.applied) || [];
    const filtered = search.trim()
      ? rows.filter((r) =>
          (r.source_title || '').toLowerCase().includes(search.toLowerCase()) ||
          (r.unit_title || '').toLowerCase().includes(search.toLowerCase())
        )
      : rows;
    return showApplied ? filtered : filtered.filter((r) => !r.applied);
  }, [locAudit, search, showApplied]);

  const gradeFlagged = useMemo(() => {
    const rows = gradeAudit?.results?.filter((r) => r.flagged || r.applied) || [];
    const filtered = search.trim()
      ? rows.filter((r) => (r.unit_title || '').toLowerCase().includes(search.toLowerCase()))
      : rows;
    return showApplied ? filtered : filtered.filter((r) => !r.applied);
  }, [gradeAudit, search, showApplied]);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6 sm:py-8" data-testid="curriculum-audit-page">
        <div className="mb-6">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">Admin</p>
          <h1 className="font-serif-display text-2xl sm:text-3xl text-[#14213D] tracking-tight">Curriculum Audit</h1>
          <p className="text-xs sm:text-sm text-[#5A6577] mt-1 max-w-2xl">
            AI-powered review of primary-source link accuracy and unit grade-level fit. Each finding is your call to accept or ignore.
          </p>
        </div>

        <Tabs defaultValue="loc" className="space-y-4">
          <TabsList className="bg-[#F9F8F4]">
            <TabsTrigger value="loc" data-testid="loc-tab">
              <ShieldCheck className="h-4 w-4 mr-2" /> LoC Link Accuracy
            </TabsTrigger>
            <TabsTrigger value="grade" data-testid="grade-tab">
              <GraduationCap className="h-4 w-4 mr-2" /> Grade Level Fit
            </TabsTrigger>
          </TabsList>

          <TabsContent value="loc" className="space-y-4">
            <StatusBanner audit={locAudit} onRun={() => runAudit('loc')} running={runningLoc} />

            {(locAudit?.status === 'complete' || (locAudit?.status === 'running' && (locAudit?.results?.length ?? 0) > 0)) && (
              <>
                <ResultsToolbar
                  search={search}
                  onSearch={setSearch}
                  showApplied={showApplied}
                  onToggleApplied={setShowApplied}
                  totalFlagged={locAudit.flagged_count || 0}
                  visible={locFlagged.length}
                />
                <div className="space-y-3">
                  {locFlagged.length === 0 ? (
                    <p className="text-sm text-[#5A6577] text-center py-6" data-testid="loc-empty">
                      {locAudit.flagged_count > 0 ? 'All matching items applied. Toggle "Show applied" to review.' : 'No mismatches found. 🎉'}
                    </p>
                  ) : (
                    locFlagged.map((r) => (
                      <LocRow
                        key={`${r.unit_id}-${r.source_index}`}
                        row={r}
                        onApply={applyLocFix}
                        showApplied={showApplied}
                      />
                    ))
                  )}
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="grade" className="space-y-4">
            <StatusBanner audit={gradeAudit} onRun={() => runAudit('grade')} running={runningGrade} />

            {(gradeAudit?.status === 'complete' || (gradeAudit?.status === 'running' && (gradeAudit?.results?.length ?? 0) > 0)) && (
              <>
                <ResultsToolbar
                  search={search}
                  onSearch={setSearch}
                  showApplied={showApplied}
                  onToggleApplied={setShowApplied}
                  totalFlagged={gradeAudit.flagged_count || 0}
                  visible={gradeFlagged.length}
                />
                <div className="space-y-3">
                  {gradeFlagged.length === 0 ? (
                    <p className="text-sm text-[#5A6577] text-center py-6" data-testid="grade-empty">
                      {gradeAudit.flagged_count > 0 ? 'All flagged units resolved.' : 'All units appear correctly graded. 🎉'}
                    </p>
                  ) : (
                    gradeFlagged.map((r) => (
                      <GradeRow key={r.unit_id} row={r} onApply={applyGradeFix} />
                    ))
                  )}
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

const ResultsToolbar = ({ search, onSearch, showApplied, onToggleApplied, totalFlagged, visible }) => (
  <div className="flex items-center justify-between gap-3 flex-wrap bg-white border border-[#EBE7E0] rounded-lg p-3">
    <div className="relative flex-1 min-w-[220px]">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#5A6577]" />
      <Input
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Filter by unit or title…"
        className="pl-8 h-8 text-xs"
        data-testid="audit-search-input"
      />
    </div>
    <p className="text-xs text-[#5A6577]">
      Showing {visible} of {totalFlagged} flagged
    </p>
    <label className="text-xs text-[#5A6577] inline-flex items-center gap-1.5 cursor-pointer">
      <input
        type="checkbox"
        checked={showApplied}
        onChange={(e) => onToggleApplied(e.target.checked)}
        data-testid="show-applied-toggle"
      />
      Show applied
    </label>
  </div>
);
